import { RefundLeftPanelSide } from 'app/src/features/Shopping/Refund/components/RefundLeftPanelSide'
import { RefundRightPanelSide } from 'app/src/features/Shopping/Refund/components/RefundRightPanelSide'
import { shoppingApi, type refundApi } from 'app/src/services/welfareportal'
import { CheckCatalogButton, DownloadFileButton, ErrorCancelPage, PortalConfirmDialog } from 'app/src/shared/components'
import { CancelSuccess } from 'app/src/shared/components/CancelSuccess'
import { euroFormatterIt, formatDate } from 'app/src/shared/helpers'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

function renderErrorBox(
  refund: refundApi.Refund,
  messages: { koOutcomeMessage: string; koOutcomeWithReasonMessage: string; canceledOutcomeMessage: string },
) {
  switch (refund.status?.value) {
    case 'REJECTED':
      return (
        <div className='mt-[30px]'>
          <div className='flex flex-col bg-[#EAEAEA] p-[20px]'>
            <div className='mt-[10px] text-sm font-normal'>
              <div className='font-bold' dangerouslySetInnerHTML={{ __html: messages.koOutcomeMessage }} />
            </div>
            {refund?.status?.message && (
              <>
                <div
                  className='mt-[10px] text-base font-normal'
                  dangerouslySetInnerHTML={{ __html: messages.koOutcomeWithReasonMessage }}
                />
                <div className='mt-[10px] text-base font-normal'>{refund?.status?.message}</div>
              </>
            )}
          </div>
        </div>
      )
    case 'CANCELED':
      return (
        <div className='mt-[30px]'>
          <div className='flex flex-col bg-[#EAEAEA] p-[20px]'>
            <div className='mt-[10px] text-sm font-normal'>
              <div className='font-bold' dangerouslySetInnerHTML={{ __html: messages.canceledOutcomeMessage }} />
            </div>
          </div>
        </div>
      )
    default:
      return <></>
  }
}

export function RefundDetails(props: { refund: refundApi.Refund; funnelMode?: boolean }) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Refund' })
  const [showModal, setShowModal] = useState(false)
  const deleteOrder = shoppingApi.useOrderCancel()
  const [errorComponentVisible, setErrorComponentVisible] = useState(false)
  const [successComponentVisible, setSuccessComponentVisible] = useState(false)

  const handleOrderCancellation = async () => {
    try {
      await deleteOrder.mutateAsync({
        pathParams: { orderId: props.refund.attributes?.orderId },
      })
      setShowModal(false)
      setSuccessComponentVisible(true)
    } catch (error) {
      setErrorComponentVisible(true)
      console.error('Failed to cancel order:', error)
    }
  }
  const errorMessages = {
    koOutcomeMessage: t('Detail.koOutcomeMessage'),
    koOutcomeWithReasonMessage: t('Detail.koOutcomeWithReasonMessage'),
    canceledOutcomeMessage: t('Detail.canceledOutcomeMessage'),
  }

  if (errorComponentVisible) {
    return (
      <ErrorCancelPage
        errorTitle={t('Detail.cancelOrder.cancelErrorMessage.title')}
        errorSubtitle={t('Detail.cancelOrder.cancelErrorMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </ErrorCancelPage>
    )
  }
  if (successComponentVisible) {
    return (
      <CancelSuccess
        successTitle={t('Detail.cancelOrder.cancelSuccessMessage.title')}
        successSubTitle={t('Detail.cancelOrder.cancelSuccessMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </CancelSuccess>
    )
  }
  if (deleteOrder.isPending) {
    return (
      <div className='mt-2 flex'>
        <div className='mx-auto'>
          <ProgressSpinner
            data-testid='loading-element'
            pt={{
              root: {
                className: 'w-[50px] h-[50px]',
              },
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='mx-auto max-w-5xl'>
      {(typeof props.funnelMode === 'undefined' || !props.funnelMode) && (
        <>
          <h1 className='welfareportal-titlecenter'>{t('refundDetailTitle')}</h1>
          {props.refund?.isCancelable && props.refund.status.value !== 'CANCELED' && (
            <div className='flex justify-end'>
              <Button
                link
                onClick={() => setShowModal(true)}
                icon='pi pi-trash'
                label={t('Detail.cancelOrder.cancelButtonLabel')}
              />
            </div>
          )}
          <div className='card grid grid-cols-1 divide-x-0 divide-y-2 p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
            <RefundLeftPanelSide statusMode refund={props.refund} />
            <RefundRightPanelSide refund={props.refund} requestedRefund={props.refund.requestedTotal} />
          </div>
          {renderErrorBox(props.refund, errorMessages)}
          <div className='mt-4 grid max-w-full rounded-md bg-[var(--card-background)] p-4'>
            <h2 className='card-detail-title overflow-wrap break-word w-full max-w-full break-words break-all'>
              {t('Detail.orderIdLabel')} {props.refund.refundId ?? '-'}
            </h2>
            <div className='my-2 h-px w-full max-w-full bg-black'></div>

            <div className='mt-2 grid grid-cols-1 md:grid-cols-2 lg:mt-4 lg:grid-cols-3'>
              <div className='mt-2 grid-rows-2'>
                <div className='uppercase'>{t('Detail.buyerDescription')}</div>
                <div className='mt-2'>
                  <h3>{props.refund.subject ? `${props.refund.subject.firstName} ${props.refund.subject.lastName}` : ''}</h3>
                </div>
              </div>
              <div className='mt-2 grid-rows-2'>
                <div className='uppercase'>{t('Detail.detailDateDescription')}</div>
                <div className='mt-2'>
                  <h3>{formatDate(props.refund.date)}</h3>
                </div>
              </div>
              <div className='mt-2 grid-rows-2'>
                <div className='uppercase'>{t('Detail.documentNumberDescription')}</div>
                <div className='mt-2'>
                  <h3 className='lowercase'>{props.refund?.invoice?.data?.invoiceId?.value ?? '-'}</h3>
                </div>
              </div>
            </div>
            <div className='mt-2 grid grid-cols-1 md:grid-cols-2 lg:mt-4 lg:grid-cols-3'>
              <div className='mt-2 grid-rows-2'>
                <div className='uppercase'>{t('Detail.documentDateDescription')}</div>
                <div className='mt-2'>
                  <h3>
                    {props.refund?.invoice?.data?.invoiceDate?.value
                      ? formatDate(props.refund?.invoice?.data?.invoiceDate?.value)
                      : '-'}
                  </h3>
                </div>
              </div>
              <div className='mt-2 grid-rows-2'>
                <div className='uppercase'>{t('Detail.vendorNameDescription')}</div>
                <div className='mt-2'>
                  <h3 className='lowercase'>{props.refund?.invoice?.data?.vendorName?.value ?? '-'}</h3>
                </div>
              </div>
              <div className='mt-2 grid-rows-2'>
                <div className='uppercase'>{t('Detail.vendorTaxIdDescription')}</div>
                <div className='mt-2'>
                  <h3 className='lowercase'>{props.refund?.invoice?.data?.vendorTaxId?.value ?? '-'}</h3>
                </div>
              </div>
            </div>
            <div className='mt-2 grid grid-cols-1 md:grid-cols-2 lg:mt-4 lg:grid-cols-3'>
              <div className='mt-2 grid-rows-2'>
                <div className='uppercase'>{t('Detail.documentAmountDescription')}</div>
                <div className='mt-2'>
                  <h3>
                    {props.refund?.invoice?.data?.invoiceTotal?.value
                      ? euroFormatterIt.format(props.refund.invoice.data.invoiceTotal.value)
                      : '-'}
                  </h3>
                </div>
              </div>
            </div>
            {props.refund.rule?.externalKeys?.find((v) => v.key === 'customerTaxId') && (
              <div className='mt-2 grid grid-cols-1 md:grid-cols-2 lg:mt-4 lg:grid-cols-3'>
                <div className='mt-2 grid-rows-2'>
                  <div className='uppercase'>
                    {props.refund.rule?.externalKeys?.find((v) => v.key === 'customerTaxId')?.label}
                  </div>
                  <div className='mt-2'>
                    <h3>{props.refund?.invoice?.data?.customerTaxId?.value}</h3>
                  </div>
                </div>
              </div>
            )}
          </div>
          {props.refund?.invoice?.documentInfo && (
            <>
              <div className='mt-8 font-bold'>{t('Detail.invoiceDescription')}</div>
              <DownloadFileButton
                buttonLabel={props.refund.invoice.documentInfo.name}
                fileLink={'.' + props.refund.invoice.documentInfo.fileUrl}
                fileName={props.refund.invoice.documentInfo.name}
              />
            </>
          )}

          {props.refund?.documents && props.refund.documents.length > 0 && (
            <>
              <div className='mt-8 font-bold'>{t('Detail.additionalDocumentsDescription')}</div>
              {props.refund.documents.map((additionalDocument: refundApi.Document) => (
                <DownloadFileButton
                  buttonLabel={additionalDocument.documentInfo.name}
                  key={additionalDocument.documentInfo?.id}
                  fileLink={'.' + additionalDocument.documentInfo.fileUrl}
                  fileName={additionalDocument.documentInfo.name}
                />
              ))}
            </>
          )}
        </>
      )}
      <div className='mx-auto flex justify-center'>
        <CheckCatalogButton />
      </div>
      {showModal && (
        <PortalConfirmDialog
          body={t('Detail.cancelOrder.body')}
          title={t('Detail.cancelOrder.title')}
          reject={() => setShowModal(false)}
          accept={handleOrderCancellation}
        />
      )}
    </div>
  )
}
